import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchDistricts, fetchFilteredDoctors } from '../Services/apiService';
import DoctorChamberSchedules from './DoctorChamberSchedules'; // Import the new component
import '../Styles/district-doctor.css';

const DistrictPage = () => {
  const [districts, setDistricts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();

  // Extract specialization from URL query params
  const specializationId = new URLSearchParams(location.search).get('specialization');

  // Fetch districts on component mount
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        setLoading(true);
        const data = await fetchDistricts();
        setDistricts(data || []);
      } catch (err) {
        setError('Failed to load districts.');
      } finally {
        setLoading(false);
      }
    };
    loadDistricts();
  }, []);

  // Fetch doctors whenever the selected district or specialization changes
  useEffect(() => {
    const loadDoctors = async () => {
      if (selectedDistrict && specializationId) {
        try {
          setLoading(true);
          setError(null); // Reset error state before a new fetch
          const data = await fetchFilteredDoctors(specializationId, selectedDistrict);
          setDoctors(data || []);
          console.log('Doctors Data:', data);
        } catch (err) {
          setError(err.message || 'Failed to load doctors.'); // Server side & client side error handling respectively
          setDoctors([]); // Clear previous doctors on error
        } finally {
          setLoading(false);
        }
      }
    };
    loadDoctors();
  }, [selectedDistrict, specializationId]);

  const handleDistrictSelect = (districtId) => {
    setSelectedDistrict(districtId);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Districts Section */}
      {districts.length > 0 && (
      <div className="py-10 px-4 max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Find Doctors by District</h2>
          <p className="text-gray-600">Select your district to view available doctors</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 max-w-md mx-auto">
          <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-3">
            Choose District
            <span className="text-blue-600 ml-1">*</span>

            <div className="relative">
              <select
                value={selectedDistrict || ''}
                onChange={(e) => handleDistrictSelect(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 text-gray-800 py-3.5 px-4 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer hover:bg-white"
              >
                <option value="" disabled className="text-gray-400">
                  Select your district
                </option>
                {districts.map((district) => (
                  <option key={district.id} value={district.id} className="py-2">
                    {district.name || 'Unnamed District'}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </label>
          <p className="text-xs text-gray-500 mt-3">
            Select from
            {districts.length}
            {' '}
            available districts
          </p>
        </div>
      </div>
      )}

      {/* Doctors Section */}
      {selectedDistrict && doctors.length > 0 && (
      <div className="py-10 px-4 max-w-6xl mx-auto">
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Available Doctors</h3>
              <p className="text-gray-600 mt-1">
                {doctors.length}
                {' '}
                doctor
                {doctors.length > 1 ? 's' : ''}
                {' '}
                found in your selected district
              </p>
            </div>
            <div className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                Real-time Availability
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {doctors.map((doctor) => (
            <div
              key={doctor.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
            >
              {/* Doctor Header */}
              <div className="p-6 border-b border-gray-50">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium mb-3">
                      ID:
                      {' '}
                      {doctor.id}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                      Dr.
                      {' '}
                      {doctor.name}
                    </h3>
                    <h4 className="text-lg font-semibold text-gray-800 mt-1">
                      ডাঃ
                      {' '}
                      {doctor.bangla_name}
                    </h4>
                  </div>
                  <div className="bg-gray-50 p-2 rounded-lg">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>

                {/* Doctor Details */}
                <div className="space-y-3">
                  <div className="flex items-center text-sm text-blue-700 font-semibold">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {doctor.specialty}
                  </div>
                  <p className="text-sm text-gray-600 flex items-start">
                    <svg className="w-4 h-4 mr-2 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                    </svg>
                    {doctor.qualification}
                  </p>
                  <p className="text-sm text-gray-600 flex items-start">
                    <svg className="w-4 h-4 mr-2 mt-0.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {doctor.experience}
                  </p>
                </div>

                {/* Contact Info */}
                <div className="mt-6 pt-6 border-t border-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center text-sm">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="font-medium text-gray-700">Phone:</span>
                      <span className="ml-2 text-gray-900">{doctor.phone}</span>
                    </div>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Order:
                      {' '}
                      {doctor.order}
                    </span>
                  </div>
                </div>
              </div>

              {/* Chambers */}
              <div className="p-6 bg-gray-50">
                <h5 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Chamber Locations
                </h5>

                <div className="space-y-4">
                  {doctor.chambers
                    ?.filter((chamber) => Number(chamber.district_id) === Number(selectedDistrict))
                    .map((chamber) => (
                      <div
                        key={chamber.id}
                        className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h6 className="font-semibold text-gray-900">{chamber.name}</h6>
                            <p className="text-sm text-gray-600 mt-1">{chamber.address}</p>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Chamber
                          </span>
                        </div>
                        <DoctorChamberSchedules doctor={doctor} chamber={chamber} />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>

  );
};

export default DistrictPage;
